import React, {useEffect, useState} from 'react';
import {
  Flex,
  Box,
  Table,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  IconButton,
  Text,
  Modal,
  ModalFooter,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Divider,
  TableContainer,
  useDisclosure,
  Button,
} from '@chakra-ui/react';
import {CustomInput, DatePicker, SubmitButton, CustomCheckbox, CustomButton} from '../../General/FormInputs';
import {useFieldArray, useForm} from 'react-hook-form';
import FlexCard from '../../General/FlexCard';
import {FaPlusSquare, FaTrashAlt} from 'react-icons/fa';
import {useClient} from '../../../views/Maintenance/ClientMaintenance';
import {post, postDelete} from '../../../service/ApiService';
import {Location as LocationUrls} from '../../../service/Urls';
import ErrorHelper from '../../../service/ErrorHelper';
import {isAdmin} from '../../../helpers/localStorage';
import BoxCard from '../../General/BoxCard';
import {Location} from '../../../constants/client/types';
import {EmptyLocation} from '../../../constants/client/defaults';
import ClientLocationSelect from '../../Location/ClientLocationSelect';
import GroupSelectNew from '../../Group/GroupSelectNew';
import LocationRegionSelect from '../../Region/LocationRegionSelect';
import {useQueryClient} from 'react-query';

export default function Locations() {
  const {client} = useClient();
  const [locationId, setLocationId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [toast, setToast] = useState('');
  const [isSalesMan, setIsSalesMan] = useState(true);
  const queryClient = useQueryClient();

  const isReadOnly = !locationId && !isAdding;

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: {errors},
  } = useForm<Location>();

  const {
    fields: aliases,
    append: appendAlias,
    remove: removeAlias,
  } = useFieldArray({
    name: 'aliases',
    control,
  });

  const {isOpen, onOpen, onClose} = useDisclosure();

  useEffect(() => {
    if (isAdmin()) setIsSalesMan(false);
  }, []);

  const refresh = () => {
    const location = client?.locations?.find(loc => loc.id == locationId) ?? EmptyLocation;
    const parsedLocation = {
      ...location,
      clientId: client?.id,
      loSince: location?.loSince?.substring(0, location.loSince.indexOf('T')) ?? '',
    };

    reset(parsedLocation);
  };

  useEffect(() => {
    if (client?.locations?.length) {
      const currentLocation = client?.locations?.find(loc => loc.id == locationId) ?? null;
      setLocationId(currentLocation?.id ?? null);

      if (currentLocation?.id === locationId) {
        refresh();
      }
    } else {
      setLocationId(null);
    }
  }, [client]);

  useEffect(() => {
    refresh();
  }, [locationId]);

  const onSubmit = async (data: Location) => {
    if (isSalesMan) {
      setToast({response: {error: '403'}, errorMessage: 'Invalid Permission'});
      return;
    }

    let response;
    if (isAdding) {
      data.loId = (Math.max(...(client?.locations?.map(({loId}) => parseInt(loId ?? '0')) ?? []), 0) + 1).toString().padStart(2, '0');
      response = await post(LocationUrls.ADD, data);
    } else {
      response = await post(LocationUrls.UPDATE + '/' + data.id, data);
    }

    if (response.isSuccess) {
      setLocationId(response.id);
      setIsAdding(false);

      queryClient.invalidateQueries('client');
      setToast({response: response, successMessage: `Successfully ${isAdding ? 'added' : 'updated'} location`});
    } else {
      setToast({response: {error: true}, errorMessage: response.error});
    }
  };

  const deleteLocation = async id => {
    const location = client?.locations?.find(location => location.id == id);
    if (confirm('Are you sure you want to remove ' + location?.loName + '?')) {
      const response = await postDelete(LocationUrls.DELETE + '/' + location?.id);

      if (response.isSuccess) {
        queryClient.invalidateQueries('client');
        setToast({response: response, successMessage: 'Successfully deleted ' + location?.loName});
      } else {
        setToast({response: {error: true}, errorMessage: response.error});
      }
    }
  };

  const addLocation = async () => {
    setIsAdding(true);
    setLocationId(null);
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setLocationId(null);
    reset({...EmptyLocation, clientId: client?.id});
  };

  const addAlias = () => {
    appendAlias({
      nseq: Math.max(...aliases.map(({nseq}) => nseq ?? 0), 0) + 1,
      calias: '',
    });
  };

  return (
    <Box mr="2">
      <Flex>
        {isAdding ? (
          <Button mb={2} colorScheme="red" onClick={cancelAdd}>
            Cancel
          </Button>
        ) : (
          <>
            <Box w="50%" mb="2" pb="2">
              <ClientLocationSelect selectedLocationId={locationId} setSelectedLocationId={setLocationId} />
            </Box>
            {client?.id && !isSalesMan ? (
              <>
                <IconButton mt="5" ml="2" colorScheme="tertiary" title="Add" size="md" icon={<FaPlusSquare />} onClick={() => addLocation()} />
                <IconButton
                  mt="5"
                  ml="2"
                  colorScheme="tertiary"
                  title="Delete"
                  size="md"
                  icon={<FaTrashAlt />}
                  onClick={() => deleteLocation(locationId)}
                />
              </>
            ) : (
              ''
            )}
          </>
        )}
      </Flex>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex>
          <FlexCard mr="2">
            <Box>
              <Flex>
                <CustomInput register={register} id="loId" name="Code" errors={errors} isReadOnly={true} w="15%" />
                <CustomInput register={register} id="loName" name="Location Name" isReadOnly={isReadOnly} errors={errors} w="90%" />
                <CustomCheckbox id="loInact" name="Inactive" control={control} isReadOnly={isReadOnly} w="30%" mt="6" />
              </Flex>
              <CustomInput register={register} id="loAdd1" name="Address" errors={errors} isReadOnly={isReadOnly} />
              <CustomInput register={register} id="loAdd2" name="Address 2" errors={errors} isReadOnly={isReadOnly} />

              <Flex>
                <CustomInput id="loZip" name="Zip" maxLength={10} isReadOnly={isReadOnly} register={register} errors={errors} required />
                <CustomInput id="loCity" name="City" isReadOnly={isReadOnly} register={register} errors={errors} />
                <CustomInput id="loState" name="State" maxLength={2} isReadOnly={isReadOnly} register={register} errors={errors} />
              </Flex>
            </Box>
          </FlexCard>
          <Box bgGradient={'linear(to-tr, blue.600, blue.200)'} boxShadow="2xl" rounded="lg" border="1px" p="2" px="30" mb="2">
            <Text fontWeight="bold" fontSize="3xl">
              Survey Types
            </Text>
            <Flex justify="space-evenly">
              <Flex flexDirection="column" px="3">
                <CustomCheckbox id="loFuneral" name="Funeral" control={control} isReadOnly={isReadOnly} w="100%" />
                <CustomCheckbox id="loCremate" name="Cremation" control={control} isReadOnly={isReadOnly} w="100%" />
                <CustomCheckbox id="loCemeter" name="Cemetery" control={control} isReadOnly={isReadOnly} w="100%" />
                <CustomCheckbox id="loMonu" name="Monument" control={control} isReadOnly={isReadOnly} w="100%" />
              </Flex>
              <Flex flexDirection="column" px="3">
                <CustomCheckbox id="loPre" name="PreNeed" control={control} isReadOnly={isReadOnly} w="100%" />
                <CustomCheckbox id="loPetcrem" name="Pet Cremation" control={control} isReadOnly={isReadOnly} w="100%" />
                <CustomCheckbox id="loEvent" name="Event Planning" control={control} isReadOnly={isReadOnly} w="100%" />
              </Flex>
            </Flex>
          </Box>
        </Flex>

        <BoxCard>
          <Flex>
            <CustomInput register={register} id="loContact" name="Contact" errors={errors} isReadOnly={isReadOnly} />
            <CustomInput register={register} id="loPhone" name="Phone" errors={errors} isReadOnly={isReadOnly} />
            <CustomInput register={register} id="loFax" name="Fax" errors={errors} isReadOnly={isReadOnly} />
            <DatePicker register={register} id="loSince" name="Location Since" errors={errors} isReadOnly={isReadOnly} required />
          </Flex>
          <Flex>
            <Box width="40%">
              <LocationRegionSelect
                isReadOnly={isReadOnly}
                name="regionId"
                setCode={(code: number) => setValue('loRegion', code)}
                control={control}
                required
              />
            </Box>
            <Box mr="3" />
            <Box width="40%">
              <GroupSelectNew groups={client?.groups ?? []} isReadOnly={isReadOnly} name="loGroup" control={control} required />
            </Box>
          </Flex>
        </BoxCard>

        <FlexCard>
          <CustomInput w="40%" register={register} id="loSscode" name="Spreadsheet Code" errors={errors} isReadOnly={isReadOnly} />
          <CustomInput w="40%" register={register} id="loRptname" name="Name For Reports" errors={errors} isReadOnly={isReadOnly} maxLength={40} />
          {location ? (
            <CustomButton
              w="60%"
              mt="5"
              name="Edit Aliases"
              onClick={() => {
                onOpen();
              }}
            />
          ) : (
            ''
          )}
        </FlexCard>
        {!isSalesMan && <SubmitButton name="Save" />}
      </form>
      <Modal blockScrollOnMount={false} size={'4xl'} isOpen={isOpen} onClose={onClose}>
        <form>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader id="header">Aliases</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Divider mt="5" colorScheme={'mint'} width={{base: '100%'}} />
              <TableContainer width={{base: '100%'}}>
                <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'}>
                  <Thead>
                    <Tr border="solid 2px #0c5f78">
                      <Th>Alias Number</Th>
                      <Th>Alias</Th>
                      <Th>
                        {!isSalesMan && (
                          <IconButton colorScheme="tertiary" title="Add" size="md" icon={<FaPlusSquare />} onClick={() => addAlias()} />
                        )}
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {aliases.map((alias, idx) => {
                      const calasId = `aliases.${idx}.calias` as const;

                      const aliasErrors = errors?.aliases?.[idx] ?? {};
                      return (
                        <Tr key={alias.id}>
                          <Td>{alias.nseq}</Td>
                          <Td>
                            <CustomInput id={calasId} errorId="calias" register={register} errors={aliasErrors} name="Alias" />
                          </Td>
                          <Td>
                            {!isSalesMan && (
                              <IconButton colorScheme="tertiary" title="Delete" size="md" icon={<FaTrashAlt />} onClick={() => removeAlias(idx)} />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </ModalBody>

            <ModalFooter></ModalFooter>
          </ModalContent>
        </form>
      </Modal>
      <ErrorHelper toastResponse={toast} />
    </Box>
  );
}
